import { jsx as _jsx } from "react/jsx-runtime";
import gsap from 'gsap';
import { useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useLocation } from 'react-router-dom';
import { mainFragmentShader, mainVertexShader } from '../../shaders/shaders';
import useDataStore from '../Data/dataStore';
import { CHANNEL } from '../Editor/EditorPreview';
import usePageStore from '../Page/pageStore';
import { getPath } from '../Router/Router';
import { editorSlug, homePath, MOBILE_SAFE_RESIZE } from '../globalUtils';
import { AnimScene, animSceneDuration, animSceneEaseEnter, animSceneEaseRapid, animSceneEaseRotateIn, animSceneEffects, asyncTimeout, baseViewHeight, cameraZ, cleanUpThree, EffectType, fontColor, getDataTexture, getInitialVertices, getLogoCurve, getVertexIds, getVerticesLogo, proteinEvent, proteinPointDistance, proteinPoints, randomAnimationSwitchEase, randomAnimationSwitchTime, ratioThreshold, renderOrder, texSize, VERTEX_COUNT, vertexGenerators, } from './animationUtils';
/**
 * Ceiling for the render resolution.
 *
 * An uncapped devicePixelRatio makes a 3x phone shade nine times the fragments
 * of a 1x screen, every frame, for as long as the page is open — which is the
 * bulk of the main-thread time PageSpeed attributes to "Other". Two is far past
 * the point where more pixels are visible on this animation.
 */
const MAX_PIXEL_RATIO = 2;
/** Used only when the driver refuses to report its own viewport limit. */
const FALLBACK_MAX_BUFFER = 4096;
const getMaxBufferDimension = (renderer) => {
    try {
        const gl = renderer?.getContext();
        if (!gl)
            return FALLBACK_MAX_BUFFER;
        const dims = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
        const limit = dims ? Math.min(dims[0], dims[1]) : 0;
        return limit > 0 ? limit : FALLBACK_MAX_BUFFER;
    }
    catch {
        return FALLBACK_MAX_BUFFER;
    }
};
/**
 * Resolution multiplier for the backing buffer, and the single source of truth
 * for it.
 *
 * Browser zoom moves devicePixelRatio and the container's CSS size in opposite
 * directions, which creates two problems this function exists to absorb:
 *
 * 1. gl_PointSize is expressed in framebuffer pixels, so the global_scale
 *    uniform is only correct while it matches the ratio the renderer actually
 *    applied. Calling setPixelRatio once at init and then setSize on every
 *    resize let the two drift apart, which is why the dots changed size as the
 *    page was zoomed.
 * 2. Zooming out grows the container in CSS pixels without bound. Once
 *    width * ratio passes the driver's MAX_VIEWPORT_DIMS the viewport is
 *    silently clamped, and the scene keeps drawing from the GL origin at the
 *    bottom-left — so it spills off the top-right of the screen.
 *
 * Pass the size the buffer is about to be given, and use the returned value for
 * both setPixelRatio and global_scale.
 */
const getPixelRatio = (width = 0, height = 0, renderer = null) => {
    const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
    const longest = Math.max(width, height);
    if (!longest)
        return ratio;
    return Math.min(ratio, getMaxBufferDimension(renderer) / longest);
};
const mouseRadiusTarget = 226;
const minGradient = 0.28;
const introShiftAmount = 2000;
const introShiftOut = 4000;
const ShaderAnimation = ({ displayedCurve, paused, iconMode, // renders just the given displayed Curve
logoAnim, // overrides the page relevant animations
eventsOnParent, // for protein point positioning
hide, allowIntroZoom, movementRotation, blockMouseInteractions, closeDisplayedCurve, restartCounter, editorMode, }) => {
    const urlPath = useLocation().pathname;
    const isEditorURL = getPath(urlPath) === editorSlug;
    const { activePage, shownPage, activeSection, pageChangeCount } = usePageStore();
    const pageSectionsRaw = useDataStore(useCallback((state) => state.data[activePage]?.page_sections, [activeSection]));
    const isBlog = useDataStore(useCallback((state) => state.data[activePage]?.isBlogPage, [activePage]));
    const pageSections = isBlog ? [] : pageSectionsRaw;
    const sectionContent = pageSections?.[activeSection];
    const inited = useRef(false);
    const isHome = getPath(urlPath) === homePath && !displayedCurve && !logoAnim;
    const activeAnim = sectionContent?.animation ||
        (isHome && !pageSections ? AnimScene.Circles : AnimScene.None);
    const firstAnimation = useRef(true);
    const activeEffects = animSceneEffects[activeAnim ?? ''];
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const renderer = useRef(null);
    const scene = useRef(null);
    const camera = useRef(null);
    const orbitControls = useRef(null);
    const composerRef = useRef(null);
    const geometry = useRef(null);
    const material = useRef();
    const projection = useRef({ width: 0, height: 0 });
    const particles = useRef(null);
    const activeEffectsRef = useRef([EffectType.Trail]);
    const activeAnimRef = useRef(null);
    const logoAnimRef = useRef(null);
    const displayedCurveRef = useRef('');
    const displayedCurveForAsync = useRef(displayedCurve);
    displayedCurveForAsync.current = displayedCurve;
    const mousePos = useRef({ x: -1000, y: -1000 });
    const mousePosTarget = useRef({ x: 0, y: 0 });
    const pausedTime = useRef(0);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    const additionalTime = useRef(0);
    const previousLogoPoints = useRef('');
    const rapidSwitch = useRef(false);
    const mounted = useRef(false);
    // Handle of the in-flight rAF, 0 when the loop is stopped. Browsers never
    // hand out 0, so it doubles as the "is the loop running" flag.
    const frameRequest = useRef(0);
    // Whether the container is on screen. Starts true: the full-screen background
    // instance always is, and the observer corrects the in-flow ones.
    const onScreen = useRef(true);
    const effectUniforms = useRef({
        [EffectType.Trail]: { value: 0 },
        [EffectType.Meta]: { value: 0 },
    });
    /*   const effectPasses = useRef<EffectPasses>({
      [EffectType.Trail]: new AfterimagePass(),
      [EffectType.Meta]: new ShaderPass(metaShader),
    }); */
    const showIntro = isHome && !pageSections;
    const uniforms = useRef({
        circles_control: { value: showIntro ? 1 : 0 },
        meta_control: { value: 0 },
        sphere_control: { value: 0 },
        logo_0_control: { value: 0 },
        logo_1_control: { value: 0 },
        logo_2_control: { value: 0 },
        logo_3_control: { value: 0 },
        logo_4_control: { value: 0 },
        logo_5_control: { value: 0 },
        protein_control: { value: 0 },
        none_control: { value: showIntro ? 0 : 1 },
        gradient: { value: minGradient },
        global_scale: { value: 1.0 },
        min_depth: { value: -500.0 },
        max_depth: { value: 500.0 },
        intro_shift: { value: introShiftAmount },
        u_noise_amp: { value: 0.5 },
        logo_0_extrude_num: { value: 0 },
        logo_0_extrude_segment: { value: 0 },
        logo_1_extrude_num: { value: 0 },
        logo_1_extrude_segment: { value: 0 },
        logo_2_extrude_num: { value: 0 },
        logo_2_extrude_segment: { value: 0 },
        logo_3_extrude_num: { value: 0 },
        logo_3_extrude_segment: { value: 0 },
        logo_4_extrude_num: { value: 0 },
        logo_4_extrude_segment: { value: 0 },
        logo_5_extrude_num: { value: 0 },
        logo_5_extrude_segment: { value: 0 },
        mouse_x: { value: -1000 },
        mouse_y: { value: -1000 },
        mouse_radius: { value: 0 },
        mouse_strength: { value: 200 },
        mouse_allowed: { value: 1 },
        vertex_count: { value: VERTEX_COUNT },
        protein_color: {
            value: new THREE.Vector3(fontColor.r, fontColor.g, fontColor.b),
        },
        protein_0: {
            value: new THREE.Vector3(100, 0, 0),
        },
        protein_1: {
            value: new THREE.Vector3(-100, 0, 0),
        },
        protein_2: {
            value: new THREE.Vector3(0, 0, 0),
        },
        protein_3: {
            value: new THREE.Vector3(0, 0, 0),
        },
        protein_4: {
            value: new THREE.Vector3(0, 0, 0),
        },
        protein_5: {
            value: new THREE.Vector3(0, 0, 0),
        },
        // this is a dangerous one, turns off final opacity, it has to be handled carefully
        final_opacity: { value: 0 },
    });
    const isLogo = (key) => key.split('_')[0] === 'logo';
    const textureUniforms = useRef({});
    const fragmentShader = useRef();
    const vertexShader = useRef();
    const onProteinPointPosition = (evt) => {
        evt.detail?.forEach((val, idx) => {
            uniforms.current[`protein_${idx}`].value.y =
                val.value * 400;
        });
    };
    useEffect(() => {
        if (!containerRef.current)
            return;
        mounted.current = true;
        window.addEventListener(MOBILE_SAFE_RESIZE, onResize);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        // hide initial container, and fade in after init
        containerRef.current.style.opacity = '0';
        if (eventsOnParent) {
            containerRef.current.parentElement?.addEventListener(proteinEvent, onProteinPointPosition);
        }
        initProjection();
        const onVisibilityChange = () => syncLoop();
        document.addEventListener('visibilitychange', onVisibilityChange);
        let observer = null;
        if (typeof IntersectionObserver !== 'undefined') {
            observer = new IntersectionObserver((entries) => {
                onScreen.current = entries.some((entry) => entry.isIntersecting);
                syncLoop();
            }, 
            // Start drawing slightly before it scrolls into view, so nothing pops.
            { rootMargin: '100px' });
            observer.observe(containerRef.current);
        }
        console.log('Inited 3D view');
        return () => {
            console.log('kill');
            mounted.current = false;
            // Without this the rAF chain outlives the component: render() starts
            // early-returning on !mounted, but animate() kept re-arming itself, so
            // every unmounted instance left a loop running for the life of the page.
            stopLoop();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            observer?.disconnect();
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            renderer.current?.dispose();
            window.removeEventListener(MOBILE_SAFE_RESIZE, onResize);
            if (eventsOnParent && containerRef.current) {
                containerRef.current?.parentElement?.removeEventListener(proteinEvent, onProteinPointPosition);
            }
            //cleanUpThree(scene.current, renderer.current, composerRef.current);
            cleanUpThree(scene.current, renderer.current, null);
        };
    }, []);
    useEffect(() => {
        if (shownPage === homePath && !displayedCurve && !iconMode) {
            if (activePage === shownPage) {
                /* Object.keys(uniforms.current).forEach((key) => {
                  const isControl = key.split('_')?.[1] === 'control';
                  if (isControl && uniforms.current[key]!.value > 0) {
                  }
                }); */
                /*activeAnimRef.current = AnimScene.Circles;
                 uniforms.current.circles_control.value = 1;
                uniforms.current.none_control.value = 0; */
                uniforms.current.intro_shift.value = introShiftAmount;
                openPage();
            }
        }
        if ((shownPage === homePath || allowIntroZoom) &&
            activePage !== shownPage) {
            closePage();
        }
    }, [shownPage, activePage, pageChangeCount]);
    useEffect(() => {
        if (paused) {
            pausedTime.current = performance.now();
        }
        else {
            if (pausedTime.current) {
                additionalTime.current += performance.now() - pausedTime.current;
                pausedTime.current = 0;
            }
        }
    }, [paused]);
    useEffect(() => {
        if (!iconMode &&
            (activeAnim !== activeAnimRef.current || isLogo(activeAnim))) {
            handleGeometryRotation();
            if (!(isLogo(activeAnim) &&
                previousLogoPoints.current === sectionContent.logo_points)) {
                activeAnimRef.current = activeAnim;
                enterAnim(activeAnim);
            }
            previousLogoPoints.current = sectionContent?.logo_points;
        }
        if (logoAnim) {
            handleLogoAnim();
        }
    }, [sectionContent, shownPage, logoAnim, pageChangeCount]);
    const handleLogoAnim = () => {
        onResize();
        if (inited.current && logoAnim && logoAnim !== logoAnimRef.current) {
            logoAnimRef.current = logoAnim;
            enterAnim(undefined, logoAnim);
        }
        else {
            enterAnim(undefined, logoAnim);
        }
    };
    useEffect(() => {
        if (displayedCurve && inited.current) {
            if (closeDisplayedCurve) {
                turnNoneOn();
            }
            else {
                turnNoneOff();
            }
        }
    }, [closeDisplayedCurve]);
    useEffect(() => {
        if (displayedCurve &&
            displayedCurveRef.current !== displayedCurve &&
            inited.current) {
            showLogo(displayedCurve);
        }
    }, [displayedCurve]);
    useEffect(() => {
        if (restartCounter && displayedCurve && inited.current) {
            showLogo(displayedCurve);
            openPage();
            fadeContainer(1);
        }
    }, [restartCounter]);
    useEffect(() => {
        if (editorMode) {
            setTimeout(() => fadeContainer(1), 100);
        }
    }, [editorMode]);
    useEffect(() => {
        if (hide !== undefined) {
            console.log('HIDE', hide);
            fadeContainer(hide ? 0 : 1);
        }
    }, [hide]);
    useEffect(() => {
        if (particles.current && movementRotation) {
            let zoomAnim = false;
            if (!movementRotation[0] && !movementRotation[1]) {
                zoomAnim = true;
            }
            const duration = 1.4;
            const split = 0.38;
            const splitDuration = duration * split;
            const multiplier = 0.4;
            if (zoomAnim) {
                if (!uniforms.current.intro_shift.value) {
                    gsap.to(particles.current.position, {
                        duration: splitDuration,
                        z: -400,
                        ease: animSceneEaseRotateIn,
                    });
                    gsap.to(particles.current.position, {
                        duration: duration - splitDuration,
                        delay: splitDuration,
                        z: 0,
                        ease: animSceneEaseRotateIn,
                    });
                }
            }
            else {
                gsap.killTweensOf(particles.current.rotation);
                gsap.to(particles.current.rotation, {
                    duration: splitDuration,
                    y: movementRotation[0] * multiplier,
                    x: movementRotation[1] * multiplier,
                    ease: animSceneEaseRotateIn,
                });
                gsap.to(particles.current.rotation, {
                    duration: duration - splitDuration,
                    delay: splitDuration,
                    y: 0,
                    x: 0,
                    ease: animSceneEaseRotateIn,
                });
            }
        }
    }, [movementRotation]);
    const turnNoneOn = () => {
        gsap.killTweensOf(uniforms.current.none_control);
        gsap.to(uniforms.current.none_control, {
            duration: 0.6,
            value: 1,
            ease: 'power2.out',
        });
    };
    const turnNoneOff = () => {
        gsap.killTweensOf(uniforms.current.none_control);
        gsap.to(uniforms.current.none_control, {
            duration: 0.6,
            value: 0,
            ease: 'power2.out',
        });
    };
    const updatePointer = (clientX, clientY) => {
        mousePosTarget.current = { x: clientX, y: clientY };
        if (mousePos.current.x < 0) {
            mousePos.current = { ...mousePosTarget.current };
            gsap.to(uniforms.current.mouse_radius, {
                duration: 2,
                value: mouseRadiusTarget,
                ease: 'power1.inOut',
            });
        }
    };
    // Mouse event.
    const onMouseMove = (event) => {
        updatePointer(event.clientX, event.clientY);
    };
    // Touch event.
    const onTouchMove = (event) => {
        if (event.touches.length > 0) {
            const touch = event.touches[0];
            updatePointer(touch.clientX, touch.clientY);
        }
    };
    const applyLogoUniforms = (curve) => {
        let lowestValue = 1;
        let lowestLogo = AnimScene.Logo_0;
        const availableLogoScene = [
            AnimScene.Logo_0,
            AnimScene.Logo_1,
            AnimScene.Logo_2,
            AnimScene.Logo_3,
            AnimScene.Logo_4,
            AnimScene.Logo_5,
        ].reduce((acc, curr, idx) => {
            const value = uniforms.current[`${curr}_control`].value;
            if (value < lowestValue) {
                lowestValue = value;
                lowestLogo = curr;
            }
            if (!value && !acc) {
                return curr;
            }
            /* if (!acc && idx === 2) {
                return curr;
              } */
            return acc;
        }, undefined) ?? lowestLogo;
        const vertices = getVerticesLogo(curve, !!iconMode && !logoAnim);
        const { vertices: extrudeVertices, setup } = getLogoCurve(curve);
        uniforms.current[`${availableLogoScene}_extrude_num`].value =
            setup.iteration ?? 0;
        uniforms.current[`${availableLogoScene}_extrude_segment`].value =
            setup.num_of_lines ?? 0;
        const vertexData = vertices;
        if (vertexData &&
            vertexData.length &&
            textureUniforms.current[`${availableLogoScene}_data`]) {
            textureUniforms.current[`${availableLogoScene}_data`].value =
                getDataTexture(vertices);
            textureUniforms.current[`${availableLogoScene}_curve_data`].value =
                getDataTexture(extrudeVertices);
        }
        return availableLogoScene;
    };
    const showLogo = (curve) => {
        uniforms.current.final_opacity.value = 1;
        if (!orbitControls.current &&
            camera.current &&
            renderer.current &&
            material.current &&
            !iconMode &&
            !blockMouseInteractions) {
            renderer.current.domElement.style.pointerEvents = 'auto';
            renderer.current.domElement.addEventListener('dblclick', () => {
                orbitControls.current?.reset();
            });
            orbitControls.current = new OrbitControls(camera.current, renderer.current.domElement);
        }
        // reset all controls
        Object.keys(uniforms.current).forEach((key) => {
            const uArr = key.split('_');
            const isControl = uArr[uArr.length - 1] === 'control';
            if (isControl) {
                gsap.killTweensOf(uniforms.current[key]);
                uniforms.current[key].value = 0;
            }
        });
        if (!logoAnim) {
            // Here we are switching between 6 logo instances randomly assigned to the task when empty, so we can tween between logos;
            const logoScene = applyLogoUniforms(curve);
            const logoKey = `${logoScene}_control`;
            gsap.killTweensOf(uniforms.current[logoKey]);
            uniforms.current[logoKey].value = 1;
        }
        if (!allowIntroZoom) {
            gsap.killTweensOf(uniforms.current.intro_shift);
            uniforms.current.intro_shift.value = 0;
        }
        uniforms.current.mouse_allowed.value = 0;
        if (!logoAnim) {
            displayedCurveRef.current = curve;
        }
        else {
            rapidSwitch.current = true;
            gsap.killTweensOf(uniforms.current['logo_0_control']);
            uniforms.current['logo_0_control'].value = 1;
            if (containerRef.current) {
                containerRef.current.style.opacity = '0';
                containerRef.current.style.transition = '';
            }
        }
        setTimeout(() => onResize(), 100);
    };
    const sendEditorHandshake = () => {
        window.opener?.postMessage({ channel: CHANNEL, type: 'handshake' }, '*');
    };
    const handleGeometryRotation = () => {
        const duration = firstAnimation.current ? 0 : 2;
        const ease = animSceneEaseEnter;
        const rotationString = sectionContent?.logo_rotation;
        if (particles.current) {
            const r = particles.current.rotation;
            if (rotationString) {
                const angles = rotationString
                    .split(',')
                    .map((val) => {
                    const num = parseFloat(val);
                    if (Number.isNaN(num)) {
                        return 0;
                    }
                    return THREE.MathUtils.degToRad(num);
                });
                gsap.to(particles.current.rotation, {
                    duration,
                    x: angles[0],
                    y: angles[1],
                    z: angles[2],
                    ease,
                });
            }
            else {
                if (r.x || r.y || r.z) {
                    gsap.to(particles.current.rotation, {
                        duration,
                        x: 0,
                        y: 0,
                        z: 0,
                        ease,
                    });
                }
            }
        }
    };
    const enterAnim = (animScene, logoAnim) => {
        let anim = animScene;
        if (animScene &&
            sectionContent &&
            !displayedCurve &&
            !logoAnim &&
            isLogo(sectionContent.animation) &&
            sectionContent.logo_points) {
            anim = applyLogoUniforms(sectionContent.logo_points);
            activeAnimRef.current = anim;
        }
        if (logoAnim) {
            anim =
                logoAnim === AnimScene.ProteinPoints
                    ? logoAnim
                    : applyLogoUniforms(logoAnim);
            activeAnimRef.current = anim;
        }
        /* const key = `${anim}_control`;
    
        if (uniforms.current[key]) {
          gsap.to(uniforms.current[key], {
            duration: animSceneDuration,
            ease: animSceneEaseEnter,
            value: 1,
          });
        } */
        if (activeAnimRef.current) {
            switchToAnim(activeAnimRef.current);
        }
    };
    const fadeContainer = (opacity) => {
        if (containerRef.current) {
            containerRef.current.style.transition = 'opacity 0.5s ease-in';
            containerRef.current.style.opacity = opacity.toString();
        }
    };
    const switchToAnim = (animScene) => {
        const isNoneAnim = animScene === AnimScene.None;
        if (hide === undefined) {
            setTimeout(() => fadeContainer(isNoneAnim ? 0 : 1), 10);
        }
        const actIdx = renderOrder.indexOf(animScene);
        let hasFullAnim = false;
        let hasAfterFull = false;
        let noneFull = false;
        let duration = rapidSwitch.current ? 1.6 : animSceneDuration;
        let ease = rapidSwitch.current ? animSceneEaseRapid : animSceneEaseEnter;
        rapidSwitch.current = false;
        if (animScene === AnimScene.ProteinPoints) {
            duration = randomAnimationSwitchTime - 0.05;
            ease = randomAnimationSwitchEase;
        }
        else if (logoAnim && eventsOnParent) {
            ease = 'power2.inOut';
        }
        gsap.killTweensOf(uniforms.current.final_opacity);
        if (animScene !== AnimScene.ProteinPoints) {
            uniforms.current.final_opacity.value = 1;
        }
        else {
            gsap.to(uniforms.current.final_opacity, {
                duration,
                onComplete: () => {
                    uniforms.current.final_opacity.value = 0;
                },
            });
        }
        //const s: any = {};
        renderOrder.forEach((anim, idx) => {
            const ctrl = `${anim}_control`;
            const val = uniforms.current[ctrl].value;
            //s[ctrl] = val;
            if (val === 1) {
                hasFullAnim = true;
                if (idx >= actIdx) {
                    hasAfterFull = true;
                }
                if (anim === AnimScene.None) {
                    noneFull = true;
                }
            }
        });
        if (firstAnimation.current && animScene && animScene !== AnimScene.None) {
            console.log('DIRECT SWITCH TO', animScene);
            uniforms.current[`${animScene}_control`].value = 1;
            uniforms.current[`${AnimScene.None}_control`].value = 0;
            firstAnimation.current = false;
        }
        if (hasAfterFull && !noneFull) {
            console.log('Apply behind tween', animScene, 1);
            uniforms.current[`${animScene}_control`].value = 1;
        }
        renderOrder.forEach((anim) => {
            const ctrl = `${anim}_control`;
            gsap.killTweensOf(uniforms.current[ctrl]);
            if (!hasFullAnim || hasAfterFull || isNoneAnim) {
                gsap.to(uniforms.current[ctrl], {
                    duration,
                    ease,
                    value: anim === animScene ? 1 : 0,
                });
            }
        });
        if (hasFullAnim && !hasAfterFull) {
            const ctrl = `${animScene}_control`;
            console.log('Apply solo tween', ctrl, 1);
            gsap.killTweensOf(uniforms.current[ctrl]);
            gsap.to(uniforms.current[ctrl], {
                duration,
                ease,
                value: 1,
                onComplete: () => {
                    console.log('### Complete logo tween', animScene);
                    renderOrder.forEach((anim) => {
                        const ctrl = `${anim}_control`;
                        uniforms.current[ctrl].value = anim === animScene ? 1 : 0;
                    });
                },
            });
            const activeIdx = renderOrder.indexOf(animScene);
            renderOrder.forEach((anim, idx) => {
                if (idx > activeIdx && anim !== animScene) {
                    const ctrl = `${anim}_control`;
                    gsap.to(uniforms.current[ctrl], {
                        duration: 0.4,
                        ease,
                        value: 0,
                    });
                }
            });
        }
        /* Object.keys(uniforms.current).forEach((key) => {
          const isControl = key.split('_')?.[1] === 'control';
          if (isControl) {
            gsap.killTweensOf(uniforms.current[key]);
            uniforms.current[key]!.value = 0;
          }
        }); */
    };
    const exitAnim = (anim) => {
        /* const key = `${anim}_control`;
        if (uniforms.current[key]) {
          gsap.to(uniforms.current[key], {
            duration: animSceneDuration,
            ease: animSceneEaseEnter,
            value: 0,
          });
        } */
    };
    const initProjection = async () => {
        onResize();
        init();
        onResize();
    };
    /* const loadAllShaders = async () => {
      fragmentShader.current = await loadShader(baseFragmentShader);
      vertexShader.current = await loadShader(baseVertexShader);
      return true;
    }; */
    const init = async () => {
        if (!containerRef.current || !canvasRef.current)
            return;
        try {
            renderer.current = new THREE.WebGLRenderer({
                canvas: canvasRef.current,
                alpha: true,
                preserveDrawingBuffer: isEditorURL,
            });
            renderer.current.outputColorSpace = THREE.SRGBColorSpace;
        }
        catch (e) {
            // TODO: Fallback animation
            return;
        }
        containerRef.current.appendChild(renderer.current.domElement);
        renderer.current.setClearColor(0x000000, 0);
        renderer.current.setPixelRatio(getPixelRatio(projection.current.width, projection.current.height, renderer.current));
        renderer.current.setSize(projection.current.width, projection.current.height);
        camera.current = new THREE.PerspectiveCamera(40, projection.current.width / projection.current.height, 5, 13500);
        camera.current.position.z = cameraZ;
        scene.current = new THREE.Scene();
        scene.current.fog = new THREE.FogExp2(0xffffff, 0.000001);
        geometry.current = new THREE.BufferGeometry();
        // await loadAllShaders();
        vertexShader.current = mainVertexShader;
        fragmentShader.current = mainFragmentShader;
        if (!vertexShader.current || !fragmentShader.current)
            return;
        //
        //   GENERATE VERTICES
        //
        const vertices = Object.keys(vertexGenerators).reduce((acc, key) => ({ ...acc, [key]: vertexGenerators[key]() }), {});
        geometry.current.setAttribute(`position`, new THREE.BufferAttribute(getInitialVertices(), 3));
        geometry.current.setAttribute(`u_id`, new THREE.BufferAttribute(getVertexIds(), 1));
        Object.keys(vertices).forEach((key) => {
            const vertexArray = vertices[key];
            textureUniforms.current[`${key}_data`] = {
                value: getDataTexture(vertexArray),
            };
            if (isLogo(key)) {
                textureUniforms.current[`${key}_curve_data`] = {
                    value: getDataTexture([]),
                };
            }
        });
        const pointsWidth = (proteinPoints.length - 1) * proteinPointDistance;
        proteinPoints.forEach((_, idx) => {
            uniforms.current[`protein_${idx}`].value = new THREE.Vector3(idx * proteinPointDistance - pointsWidth / 2);
        });
        material.current = new THREE.ShaderMaterial({
            uniforms: {
                u_time: { value: 0.0 },
                texture_resolution: { value: new THREE.Vector2(texSize, texSize) },
                ...uniforms.current,
                ...textureUniforms.current,
            },
            vertexShader: vertexShader.current,
            fragmentShader: fragmentShader.current,
            transparent: true,
            depthTest: false,
            /*
            depthTest: true,
            depthWrite: true,
            alphaTest: 0.1,
            depthWrite: false,
            premultipliedAlpha: false,
             blending: THREE.CustomBlending,
            blendSrc: THREE.OneFactor,
            blendDst: THREE.OneMinusSrcAlphaFactor, */
        });
        material.current.needsUpdate = true;
        particles.current = new THREE.Points(geometry.current, material.current);
        // // Post-processing setup
        // const composer = new EffectComposer(renderer.current);
        // effectPasses.current.trail = new AfterimagePass();
        // const renderScene = new RenderPass(scene.current, camera.current);
        // effectPasses.current.trail.uniforms['damp'] = effectUniforms.current.trail;
        // effectPasses.current.meta.renderToScreen = true; // Ensure this pass outputs to screen.
        // effectPasses.current.meta.uniforms['effect_amount'] =
        //   effectUniforms.current.meta;
        // effectPasses.current.trail.enabled = false;
        // effectPasses.current.meta.enabled = false;
        // composer.addPass(renderScene);
        // composer.addPass(effectPasses.current.trail);
        // composer.addPass(effectPasses.current.meta);
        // composerRef.current = composer;
        scene.current.add(particles.current);
        applyResize();
        startLoop();
        if (!iconMode && !logoAnim) {
            openPage();
            sendEditorHandshake();
        }
        await asyncTimeout();
        inited.current = true;
        if (displayedCurveForAsync.current) {
            showLogo(displayedCurveForAsync.current);
        }
        if (logoAnim) {
            showLogo(logoAnim);
        }
        fadeContainer(1);
        setTimeout(() => (firstAnimation.current = false), 500);
        /* if (shownPage === homePath){
          openPage();
        } */
        /* if (logoAnim) {
          handleLogoAnim();
        } */
    };
    const openPage = () => {
        if (shownPage === homePath || (isHome && !pageSections) || allowIntroZoom) {
            gsap.set(containerRef.current, { opacity: 0 });
            fadeContainer(1);
            gsap.killTweensOf(uniforms.current.intro_shift);
            uniforms.current.intro_shift.value = introShiftAmount;
            gsap.to(uniforms.current.intro_shift, {
                duration: 2,
                value: 0,
                ease: 'power2.out',
            });
        }
    };
    const closePage = () => {
        console.log('CLOSE');
        gsap.to(uniforms.current.intro_shift, {
            duration: 0.6,
            value: -introShiftOut,
            ease: 'power2.in',
        });
        fadeContainer(0);
    };
    const animate = () => {
        render();
        frameRequest.current = requestAnimationFrame(animate);
    };
    const startLoop = () => {
        if (frameRequest.current || !mounted.current)
            return;
        frameRequest.current = requestAnimationFrame(animate);
    };
    const stopLoop = () => {
        if (!frameRequest.current)
            return;
        cancelAnimationFrame(frameRequest.current);
        frameRequest.current = 0;
    };
    // Draw only while there is something to draw for. Both gates matter for
    // different instances: the fixed full-screen background is never off screen
    // but does go into background tabs, while the in-flow instances (product card
    // logos, RandomShaderAnimation) scroll away while the tab stays visible.
    const syncLoop = () => {
        if (onScreen.current && !document.hidden)
            startLoop();
        else
            stopLoop();
    };
    const applyResize = () => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds)
            return;
        projection.current = {
            width: bounds.width,
            height: bounds.height,
        };
        if (camera.current && renderer.current && particles.current) {
            const { width, height } = projection.current;
            const cameraDistance = cameraZ; // Distance from camera to target
            const aspect = width / height;
            let effectiveViewHeight;
            if (aspect < ratioThreshold) {
                effectiveViewHeight = baseViewHeight / (aspect / ratioThreshold);
            }
            else {
                effectiveViewHeight = baseViewHeight;
            }
            const newFov = 2 *
                THREE.MathUtils.radToDeg(Math.atan(effectiveViewHeight / 2 / cameraDistance));
            // Update the camera parameters:
            camera.current.fov = newFov;
            camera.current.aspect = aspect;
            camera.current.updateProjectionMatrix();
            // Re-derive the ratio here, not just at init: browser zoom changes both
            // devicePixelRatio and the container size, and global_scale below has to
            // match whatever the renderer ends up using.
            const pixelRatio = getPixelRatio(width, height, renderer.current);
            renderer.current.setPixelRatio(pixelRatio);
            renderer.current.setSize(width, height);
            /* uniforms.current.global_position_scale.value =
              width > height
                ? 1
                : width / height; */
            const screenRatio = height < width ? height : width;
            // console.log('>>>>', width, height, effectiveViewHeight);
            uniforms.current.global_scale.value = screenRatio * pixelRatio;
            /* uniforms.current.global_scale.value =
              projection.current.width > projection.current.height
                ? (projection.current.height / 1024) * screenRatio
                : (projection.current.width / 1024) * screenRatio; */
            /* const scales = particles.current.geometry.attributes.scale.array;
            for (let i = 0; i < scales.length; i++) {
              scales[i] = window.innerHeight * scaleToHeight;
            }
            particles.current.geometry.attributes.scale.needsUpdate = true; */
            if (iconMode) {
                render(true);
            }
        }
    };
    const onResize = () => {
        requestAnimationFrame(applyResize);
    };
    const onContainerRef = (ref) => {
        if (!containerRef.current) {
            containerRef.current = ref;
            applyResize();
            if (iconMode) {
                setTimeout(applyResize, 400);
            }
        }
    };
    const render = (force) => {
        if (!camera.current ||
            !scene.current ||
            !renderer.current ||
            !particles.current ||
            !material.current ||
            !mounted.current)
            return;
        //console.log('----- render');
        if (!force && pausedRef.current)
            return;
        if (!force && uniforms.current.none_control.value > 0.999) {
            uniforms.current.none_control.value = 1;
            return;
        }
        // in case of force render save time
        if (pausedRef.current) {
            pausedTime.current = performance.now();
        }
        // console.log('render', uniforms.current.none_control);
        const delay = 0.046;
        const { x, y } = mousePosTarget.current;
        mousePos.current = {
            x: mousePos.current.x + (x - mousePos.current.x) * delay,
            y: mousePos.current.y + (y - mousePos.current.y) * delay,
        };
        const { height, width } = projection.current;
        const mouseFactor = 1100;
        const mouse_x = ((mousePos.current.x - width / 2) / height) * mouseFactor;
        const mouse_y = (-(mousePos.current.y - height / 2) / height) * mouseFactor;
        uniforms.current.mouse_x.value = mouse_x;
        uniforms.current.mouse_y.value = mouse_y;
        uniforms.current.gradient.value = Math.max(effectUniforms.current.meta.value, minGradient);
        material.current.uniforms.u_time.value =
            (performance.now() - additionalTime.current) / 1000;
        renderer.current.render(scene.current, camera.current);
    };
    return (_jsx("div", { className: "background-animation", ref: onContainerRef, onClick: () => console.log('++++++++', uniforms.current), children: _jsx("canvas", { ref: canvasRef }, "background-animation-canvas") }, "background-animation"));
};
export default ShaderAnimation;
